import type * as hast from 'hast';

import {
    MARK_BOLD,
    MARK_CODE,
    MARK_COLOR,
    MARK_ITALIC,
    MARK_KEYBOARD,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
} from '@gitbook/doc-core';
import type { Text } from '@gitbook/slate';

import { formatMarkColorStyle, transformTextToNode } from '../utils';
import type { GastToHastTransformFn } from './types';

/**
 * Transform a text from the GitBook document into hast.
 */
export const transformText: GastToHastTransformFn<Text> = async function transformText(node) {
    return transformTextToNode(node, {
        transformTextBreak: () => {
            return [{ type: 'element', tagName: 'br', children: [] }];
        },
        transformMark: (mark, children) => {
            switch (mark.type) {
                case MARK_BOLD:
                    return [
                        {
                            type: 'element',
                            tagName: 'strong',
                            children,
                        },
                    ];
                case MARK_ITALIC:
                    return [
                        {
                            type: 'element',
                            tagName: 'em',
                            children,
                        },
                    ];
                case MARK_STRIKETHROUGH:
                    return [
                        {
                            type: 'element',
                            tagName: 'del',
                            children,
                        },
                    ];
                case MARK_SUBSCRIPT:
                    return [
                        {
                            type: 'element',
                            tagName: 'sub',
                            children,
                        },
                    ];
                case MARK_SUPERSCRIPT:
                    return [
                        {
                            type: 'element',
                            tagName: 'sup',
                            children,
                        },
                    ];
                case MARK_KEYBOARD:
                    return [
                        {
                            type: 'element',
                            tagName: 'kbd',
                            children,
                        },
                    ];
                case MARK_COLOR: {
                    const style = formatMarkColorStyle(mark);
                    if (!style) {
                        return children;
                    }
                    return [
                        {
                            type: 'element',
                            tagName: 'mark',
                            properties: {
                                style,
                            },
                            children,
                        },
                    ];
                }
                case MARK_CODE: {
                    const value = children
                        // @ts-expect-error
                        .map((node) => (node.type === 'text' ? node.value : ''))
                        .join('');
                    return [
                        {
                            type: 'element',
                            tagName: 'code',
                            children: [{ type: 'text', value }],
                        },
                    ];
                }
                default:
                    return children;
            }
        },
    }) as Array<hast.Text | hast.Element>;
};
